import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import EFPicture from 'elem/compCommon/EFPicture';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import EFVideoModal from '@/components/UI/EFVideoModal';

const NavArrow = ({ direction }: { direction: 'prev' | 'next' }) => (
    <svg
        className="oc-slider__nav-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        aria-hidden="true"
    >
        <path
            d={direction === 'prev' ? 'M17.5 21L10.5 14L17.5 7' : 'M10.5 7L17.5 14L10.5 21'}
            stroke="currentColor"
            strokeWidth="2.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const PlayIcon = () => (
    <span className="oc-slider__play" aria-hidden="true">
        <svg className="oc-slider__play-pc" xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
            <path
                d="M39.645 5C20.51 5 5 20.5275 5 39.68C5 58.83 20.51 74.3575 39.645 74.3575C58.7775 74.3575 74.29 58.83 74.29 39.68C74.29 20.5275 58.7775 5 39.645 5ZM39.645 70.8913C22.4237 70.8913 8.4625 56.9162 8.4625 39.68C8.4625 22.4375 22.425 8.46625 39.645 8.46625C56.8662 8.46625 70.8275 22.4412 70.8275 39.68C70.8275 56.9162 56.8662 70.8913 39.645 70.8913ZM55.37 37.2225C49.9812 33.55 40.2075 28.0525 34.6375 24.65C32.3537 23.205 30.4362 23.7837 30.2512 26.33C30.1162 33.1462 30.2512 46.2925 30.2512 53.23C30.3637 55.8112 32.6412 56.1525 34.62 55.1113L55.325 42.5575C55.2862 42.5363 59.565 40.1775 55.3712 37.2225H55.37Z"
                fill="white"
            />
        </svg>
        <svg className="oc-slider__play-m" xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path
                d="M17.8403 2.25C9.2295 2.25 2.25 9.23737 2.25 17.856C2.25 26.4735 9.2295 33.4609 17.8403 33.4609C26.4499 33.4609 33.4305 26.4735 33.4305 17.856C33.4305 9.23737 26.4499 2.25 17.8403 2.25ZM17.8403 31.9011C10.0907 31.9011 3.80813 25.6123 3.80813 17.856C3.80813 10.0969 10.0912 3.80981 17.8403 3.80981C25.5898 3.80981 31.8724 10.0986 31.8724 17.856C31.8724 25.6123 25.5898 31.9011 17.8403 31.9011ZM24.9165 16.7501C22.4916 15.0975 18.0934 12.6236 15.5869 11.0925C14.5592 10.4422 13.6963 10.7027 13.6131 11.8485C13.5523 14.9158 13.6131 20.8316 13.6131 23.9535C13.6637 25.1151 14.6886 25.2686 15.579 24.8001L24.8962 19.1509C24.8788 19.1413 26.8043 18.0799 24.9171 16.7501H24.9165Z"
                fill="white"
            />
        </svg>
    </span>
);

export default function P1() {
    const { useLang, useRouter, useState, useEffect, useRef } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('vanliferoadtrips');
    const trackRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef({
        active: false,
        moved: false,
        suppressClick: false,
        startX: 0,
        scrollLeft: 0,
        video: '',
    });
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);
    const [source, setSource] = useState('');
    const LOCAL_SITES: string[] = ['de', 'za', 'fr', 'it', 'es', 'eu', 'uk', 'au'];
    const COVER_OVERRIDES: Record<string, string[]> = {
        de: [
            'https://websiteoss.ecoflow.com/figma-uploads/1787281518355-5th16g-compressed-Frame%2029.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787281531959-blo9sa-compressed-Frame%202094654921.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787281547585-koom37-compressed-Frame%202094654922.webp',
        ],
        it: [
            'https://websiteoss.ecoflow.com/figma-uploads/1787280855318-fhh236-compressed-it-1.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787280909766-wbw3gs-compressed-it-2.webp',
        ],
        eu: [
            'https://websiteoss.ecoflow.com/figma-uploads/1787280922065-8nijkb-compressed-eu-1.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787280930818-bphkj9-compressed-eu-2.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787280943584-50zpov-compressed-eu-3.webp',
        ],
        fr: [
            'https://websiteoss.ecoflow.com/figma-uploads/1787280958086-u5g6ck-compressed-fr-1.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787280968490-weit6i-compressed-fr-2.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787280978937-b32zd5-compressed-fr-3.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787280988724-hmgqjh-compressed-fr-4.webp',
        ],
        es: [
            'https://websiteoss.ecoflow.com/figma-uploads/1787281003712-v8hjno-compressed-es-1.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787281019397-a9iy9a-compressed-es-2.webp',
        ],
        uk: [
            'https://websiteoss.ecoflow.com/figma-uploads/1787281031659-ssyo17-compressed-uk-1.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787281044028-hc7in3-compressed-uk-2.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787281054469-dbdobb-compressed-uk-3.webp',
            'https://websiteoss.ecoflow.com/figma-uploads/1787281065227-kwc1b9-compressed-uk-4.webp',
        ],
    };
    const items: any = [
        {
            img: '/responsive/picture/vanlife-roadtrips/15_1',
            desc: t('secP_list1'),
            video: (() => {
                const map: any = {
                    us: 'https://www.youtube.com/embed/YUYklvdjnis?si=oaQEG1FLJCdIonu8',
                    de: 'https://www.youtube.com/embed/BljtM9_XTs4?si=qip5mfIvNfVaQwbg',
                    za: 'https://www.youtube.com/embed/xLcCU29riWE?si=za-NI7Ghpi12c87Z',
                    fr: 'https://www.youtube.com/embed/jvtgKyf0peY?si=0PGuiNljObyzV5sT',
                    uk: 'https://www.youtube.com/embed/YUYklvdjnis?si=cPO6Lb8atNEo4f9w',
                    eu: 'https://www.youtube.com/embed/a2npZ_toi7U?si=xGDwvG2R0WO_ged6',
                    it: 'https://www.youtube.com/embed/R_dvRU7S-9g?si=iONPtsuEvEztNir-',
                    es: 'https://www.youtube.com/embed/JKo_Gqg-PPA?si=nSmASd0qPdUEizQc',
                    au: 'https://www.youtube.com/embed/3uO9fLnurB8?si=VwxOkrGNLAoDpsIs',
                    kr: 'https://www.youtube.com/embed/YUYklvdjnis?si=oaQEG1FLJCdIonu8',
                };
                return map[locale] || map.us;
            })(),
        },
        {
            img: '/responsive/picture/vanlife-roadtrips/15_2',
            desc: t('secP_list2'),
            video: (() => {
                const map: any = {
                    us: 'https://www.youtube.com/embed/cLUIyKrd9ts?si=LAhDmbIF9XVledOO',
                    de: 'https://www.youtube.com/embed/r4VF3jWhdss?si=2lqTEkl-TxDMxABM',
                    za: 'https://www.youtube.com/embed/cLUIyKrd9ts?si=C8VPQT-30jqwfYZk',
                    fr: 'https://www.youtube.com/embed/f-OCUmzlJ2M?si=cl-zwcaIbcTs_G80',
                    uk: 'https://www.youtube.com/embed/lnBFlLk0jiA?si=DSb5ZfBw0zIQd46Z',
                    eu: 'https://www.youtube.com/embed/Zlaw1AwPGIo?si=lfwr8gspNpquxoXE',
                    it: 'https://www.youtube.com/embed/AYCXJWGLx4k?si=ZFv07lpGKV3C0JTy',
                    es: 'https://www.youtube.com/embed/EtX5nt4vMMI?si=evxynfnT-2Nhrm9b',
                    au: 'https://www.youtube.com/embed/EYAPKD1_ye0?si=HljDwFVKCeLK848l',
                    kr: 'https://www.youtube.com/embed/cLUIyKrd9ts?si=LAhDmbIF9XVledOO',
                };
                return map[locale] || map.us;
            })(),
        },
        {
            img: '/responsive/picture/vanlife-roadtrips/15_3',
            desc: t('secP_list3'),
            video: (() => {
                const map: any = {
                    us: 'https://www.youtube.com/embed/3TEoBjHcCi0?si=OyoeXmhPZd3sLZiT',
                    de: 'https://www.youtube.com/embed/XtN6-CLbzt8?si=rld25Okx-R5_40-x',
                    za: 'https://www.youtube.com/embed/ULol9L_07_E?si=qi0WrQYGkVytYIKX',
                    fr: 'https://www.youtube.com/embed/NwA31h4QAvM?si=RxXm_MwT_ZmAhIaT',
                    uk: 'https://www.youtube.com/embed/cLUIyKrd9ts?si=t2jxORIwTSwjto0n',
                    eu: 'https://www.youtube.com/embed/Au6hRX3eiik?si=1plM9b-nx37d9sSx',
                    au: 'https://www.youtube.com/embed/MxSV3RdvwsU?si=M8g5Sx6whvibKwFd',
                    kr: 'https://www.youtube.com/embed/3TEoBjHcCi0?si=OyoeXmhPZd3sLZiT',
                };
                return map[locale] || map.us;
            })(),
        },
        {
            img: '/responsive/picture/vanlife-roadtrips/15_4',
            desc: t('secP_list4'),
            video: (() => {
                const map: any = {
                    us: 'https://www.youtube.com/embed/Au6hRX3eiik?si=1fduvHMlUslHedt6',
                    de: 'https://www.youtube.com/embed/1SpIn7D11nI?si=VNxyMOfH3bJKn3l1',
                    za: 'https://www.youtube.com/embed/a2npZ_toi7U?si=ox5od9JlDnPJwiaK',
                    fr: 'https://www.youtube.com/embed/PzczrIeflPs?si=7xIuWKtJlNZJizlK',
                    uk: 'https://www.youtube.com/embed/WHSVYt-_hPM?si=GQyq3dX_gAcIYzB5',
                    au: 'https://www.youtube.com/embed/KHSKg0hRjxU?si=LsEShSkOSELZAex8',
                    kr: 'https://www.youtube.com/embed/Au6hRX3eiik?si=1fduvHMlUslHedt6',
                };
                return map[locale] || map.us;
            })(),
        },
    ];

    const visibleItems = ['us', 'ca', 'kr'].includes(locale)
        ? [items[0], items[1], items[3]]
        : ['eu', 'de'].includes(locale)
        ? items.slice(0, 3)
        : ['it', 'es'].includes(locale)
        ? items.slice(0, 2)
        : items;

    const updateNav = () => {
        const el = trackRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanPrev(scrollLeft > 1);
        setCanNext(scrollLeft + clientWidth < scrollWidth - 1);
    };

    const scrollByDir = (dir: -1 | 1) => {
        const el = trackRef.current;
        if (!el) return;
        const item = el.querySelector('.oc-slider__item') as HTMLElement | null;
        const gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap) || 12;
        const step = (item?.offsetWidth || 600) + gap;
        el.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'touch') return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        const el = trackRef.current;
        if (!el) return;

        const item = (e.target as HTMLElement | null)?.closest?.('.oc-slider__item') as HTMLElement | null;

        dragRef.current = {
            active: true,
            moved: false,
            suppressClick: false,
            startX: e.clientX,
            scrollLeft: el.scrollLeft,
            video: item?.dataset?.video || '',
        };
        el.setPointerCapture?.(e.pointerId);
        el.classList.add('is-dragging');
    };

    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        const el = trackRef.current;
        if (!drag.active || !el) return;

        const dx = e.clientX - drag.startX;
        if (Math.abs(dx) > 5) {
            drag.moved = true;
        }
        el.scrollLeft = drag.scrollLeft - dx;
    };

    const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
        const el = trackRef.current;
        if (!dragRef.current.active) return;

        const { moved, video } = dragRef.current;
        dragRef.current.active = false;
        dragRef.current.moved = false;
        dragRef.current.video = '';
        // 拖拽后吞掉随后的 click；未拖拽时 pointerup 已开视频，也吞掉重复 click
        dragRef.current.suppressClick = true;
        el?.releasePointerCapture?.(e.pointerId);
        el?.classList.remove('is-dragging');

        // setPointerCapture 会让 click 落在 track 上，卡片 onClick 收不到；未拖拽时在 pointerup 打开视频
        if (!moved && video) {
            setSource(video);
        }
    };

    const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
        // 桌面：pointerup 已处理开视频 / 拖拽，这里吞掉后续 click
        if (dragRef.current.suppressClick) {
            e.preventDefault();
            e.stopPropagation();
            dragRef.current.suppressClick = false;
            return;
        }

        // 触屏：未走桌面拖拽逻辑，用 click 打开视频
        const item = (e.target as HTMLElement | null)?.closest?.('.oc-slider__item') as HTMLElement | null;
        const video = item?.dataset?.video;
        if (video) {
            setSource(video);
        }
    };

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;

        updateNav();
        el.addEventListener('scroll', updateNav, { passive: true });
        window.addEventListener('resize', updateNav);

        return () => {
            el.removeEventListener('scroll', updateNav);
            window.removeEventListener('resize', updateNav);
        };
    }, [visibleItems.length]);

    return (
        <div id="vanlife-roadtrips-P" className={`vanlife-roadtrips-P1 ${css['vanlife-roadtrips-P1']}`}>
            <div className="oc-slider">
                <div className="oc-slider__content">
                    <div className="oc-slider__header">
                        <p className="oc-slider__title">{t('secP_title')}</p>
                    </div>
                    <div
                        className="oc-slider__track"
                        ref={trackRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                        onClickCapture={handleClickCapture}
                    >
                        {visibleItems?.map((card: any, k: number) => (
                            <div
                                className={`oc-slider__item oc-slider__item--${k + 1}`}
                                key={k}
                                data-video={card?.video}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSource(card?.video);
                                    }
                                }}
                            >
                                <div className="oc-slider__media">
                                    <EFPicture
                                        className="oc-slider__img"
                                        src={
                                            COVER_OVERRIDES[locale]?.[k] ||
                                            `${card?.img}${LOCAL_SITES.includes(locale) ? `_${locale}` : ''}.jpg`
                                        }
                                        alt={card.desc}
                                    />
                                    <PlayIcon />
                                </div>
                                <div className="oc-slider__body">
                                    <p className="oc-slider__desc">{card?.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="oc-slider__nav">
                        <button
                            type="button"
                            className={`oc-slider__nav-btn oc-slider__nav-btn--prev${!canPrev ? ' is-disabled' : ''}`}
                            aria-label="Previous"
                            disabled={!canPrev}
                            onClick={() => scrollByDir(-1)}
                        >
                            <NavArrow direction="prev" />
                        </button>
                        <button
                            type="button"
                            className={`oc-slider__nav-btn oc-slider__nav-btn--next${!canNext ? ' is-disabled' : ''}`}
                            aria-label="Next"
                            disabled={!canNext}
                            onClick={() => scrollByDir(1)}
                        >
                            <NavArrow direction="next" />
                        </button>
                    </div>
                </div>
            </div>
            {Boolean(source) && (
                <EFVideoModal
                    visible={Boolean(source)}
                    source={source}
                    onClose={() => setSource('')}
                    playerType={locale === 'cn' ? 'Alibaba' : 'YouTube'}
                />
            )}
        </div>
    );
}
